---
title: "Backtesting and Forward-Testing a Strategy"
slug: backtesting-forward-testing
shortTitle: "Backtesting & Forward-Testing"
description: "How to test a trading strategy before real money is at stake: write testable rules, backtest on past charts, measure expectancy after costs, and forward-test on demo first."
level: advanced
category: trading
course: advanced
order: 10
topics: [trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/backtesting-forward-testing.svg
faqs:
  - q: "What is the difference between backtesting and forward-testing?"
    a: "Backtesting applies a strategy's written rules to past price data to see how it would have done. Forward-testing applies the same rules to new prices as they arrive, on a demo account or with very small size, so hindsight cannot creep in."
  - q: "How many trades do I need for a meaningful test?"
    a: "There is no magic number, but a few dozen trades can easily be skewed by luck. Many traders aim for at least 100 backtested trades across different market conditions, then a further 30 to 50 forward-tested trades."
  - q: "What is overfitting?"
    a: "Overfitting means tuning a strategy's settings until they match past data almost perfectly. The result usually reflects the quirks of that period rather than a real edge, and it tends to perform much worse on new data."
  - q: "Does a profitable backtest mean the strategy will make money?"
    a: "No. A backtest shows how the rules behaved on past data under your assumptions. Markets change, costs and slippage can be higher than expected, and trading real money feels different, so results going forward can be much worse."
  - q: "Can I backtest on the {{BRAND}} chart?"
    a: "You can scroll back through price history on the trade chart and step through it manually. The trade chart has no indicators or drawing tools, so rules that need them are usually tested in a separate charting tool."
---

Every strategy starts as an idea, and most ideas sound convincing until they meet real prices. **Backtesting** applies a strategy's written rules to past price data to see how it would have performed. **Forward-testing** applies the same rules to new prices as they arrive, on a demo account or with very small size, where hindsight cannot help you. Together they answer two questions: did this idea have an edge, and can you actually trade it?

This lesson walks through both, from writing rules that can be tested to measuring results after costs and spotting the traps that make a backtest look better than reality. All numbers are illustrative. A test describes the past under your assumptions; it does not predict what comes next.

<CTA />

<KeyTakeaways>

- **No written rules, no test.** If a stranger could not follow your entry, stop, exit and sizing rules, you cannot test them either.
- **Measure in R, after costs.** Expectancy per trade, drawdown and losing streaks matter more than win rate, and spreads, fees and swap can erase a thin edge.
- **Most backtests flatter the strategy.** Overfitting, hindsight, small samples and missing costs all push results upward.
- **Forward-test before you scale.** Run the frozen rules on demo, then at small size, and compare the numbers with the backtest.

</KeyTakeaways>

## Two tests, two questions

<CompareTable caption="Backtesting and forward-testing compared">

| | Backtesting | Forward-testing |
| --- | --- | --- |
| Data | Past prices | New prices, in real time |
| Question it answers | Did the rules have an edge before? | Does the edge hold, and can I execute it? |
| Speed | Months of data in hours | As slow as the market |
| Main weakness | Hindsight and overfitting | Takes time; small samples at first |
| Where | Charts, spreadsheets, code | A demo account, then small live size |

</CompareTable>

## Step 1: Write rules that can be tested

Vague rules ("buy strong breakouts") let you judge each trade with hindsight. Testable rules leave nothing to interpret. Here is an illustrative example:

- **Market and timeframe:** BTC/USDT, 4h candles.
- **Entry:** buy when a candle closes above the highest high of the previous 20 candles.
- **Stop:** just below the lowest low of the last 5 candles.
- **Exit:** take profit at 2R, where 1R is the distance from entry to stop.
- **Size:** risk 1% of equity per trade, one open trade at a time.
- **Filter:** no new trades within 24 hours of a high-impact economic event.

Each rule should be specific enough that two people testing it would record the same trades. The [breakout vs mean reversion](/academy/article/breakout-vs-mean-reversion) lesson has more ideas you could turn into rules.

## Step 2: Run the backtest

A **manual backtest** means scrolling back through history, hiding the candles to the right, and stepping forward one candle at a time. Each time the rules fire, you record the date, direction, entry, stop, target, exit and the result in R in a spreadsheet. It is slow, but it teaches you how the strategy feels.

An **automated backtest** runs the rules in code or a spreadsheet over downloaded price data. It is much faster and can test thousands of trades, but a single coding mistake can quietly produce wonderful, false results.

On {{BRAND}}, the trade chart offers 1m, 5m, 15m, 1h, 4h and 1D timeframes, and you can scroll back through history, then use the **Back to latest** pill to return. The trade chart has no indicators or drawing tools, so rules that rely on them are usually tested in a separate charting tool.

{/* TODO: verify with platform — how much price history the trade chart loads per timeframe. */}

Aim for a sample that covers different conditions: trends, ranges, quiet and volatile periods. Many traders want at least 100 trades before they trust the numbers.

## Step 3: Measure what matters

<Example title="Scoring 80 backtested trades (illustrative)">

The test produced 80 trades: 30 winners averaging +2.1R and 50 losers averaging −1R.

- Win rate = 30 ÷ 80 = **37.5%**.
- Expectancy = (0.375 × 2.1) − (0.625 × 1) = 0.7875 − 0.625 = 0.1625, about **+0.16R** per trade.
- Profit factor (gross wins ÷ gross losses) = (30 × 2.1) ÷ (50 × 1) = 63 ÷ 50 = **1.26**.
- Now add costs. If the spread and fee average 0.08R per trade, net expectancy = 0.1625 − 0.08 = 0.0825, about **+0.08R**: half the edge is gone.
- Risking 50 USDT per trade (1% of 5,000), +0.0825R is about 0.0825 × 50 = **4.13 USDT** per trade on average, or roughly 330 USDT over 80 trades, with drawdowns along the way.

</Example>

Also record the **maximum drawdown** and the **longest losing streak**: they tell you what living with the strategy will feel like. [Drawdown, expectancy and Kelly](/academy/article/drawdown-expectancy-kelly) explains how these numbers feed into how much to risk.

## The traps that flatter a backtest

- **Overfitting.** Tuning many settings until the past looks perfect. Keep rules few and round, and check the result on a stretch of data you did not use to build them.
- **Look-ahead bias.** Using information you would not have had at the time, such as a candle's close before it closed, or a level only obvious afterwards.
- **Survivorship bias.** Testing only on markets that are still around or did well, which quietly removes the failures.
- **Missing costs.** Leaving out the spread, the trade fee ({{TRADE_FEE}}), swap on positions held past {{SWAP_HOUR}}, and slippage when a stop fills at market.
- **One kind of market.** A strategy tested only in a strong trend says little about how it handles a range.
- **Different data.** CFD prices follow a reference price plus a spread, so they can differ slightly from exchange data you tested on.

<Warning title="A good test is not a promise">

Even a careful backtest only shows how rules behaved on past data. Markets change, live execution is messier, and leverage magnifies whatever losses come. Most traders who trade CFDs lose money. Start live with small size, whatever your test says.

</Warning>

## Step 4: Forward-test on demo, then small live

A demo account lets you run the strategy in real time on {{DEMO_BALANCE}} instead of real money. On desktop, you can request one with the **Demo** button in the top bar.

{/* TODO: verify with platform — demo account availability and how it is requested. */}

<Steps>
<Step title="Freeze the rules">

Label them, for example "v1.0", and do not change them during the test. Any change starts a new test.

</Step>
<Step title="Set the pass mark in advance">

Decide the number of trades and the criteria now: for example, 40 trades, expectancy above zero after costs, and no drawdown worse than 10R.

</Step>
<Step title="Trade and log everything">

Place every signal the rules give, with the planned TP / SL, and note any you skipped and why. Skipped signals are data too.

</Step>
<Step title="Compare with the backtest">

Use **Net PnL**, **Fees**, **Swap** and **Reason** in **Closed Positions**, and the win ratio, average profit and average loss on the **Portfolio** page, to see whether the numbers sit in the same range.

</Step>
<Step title="Go live small">

If the results hold, trade the same rules live at a fraction of your intended size. Real money changes how you execute, and that needs testing too.

</Step>
</Steps>

<ProTip title="Treat rule changes like new strategies">

It is tempting to tweak a rule after each painful loss. Instead, keep a list of proposed changes and test them together as v1.1 after the current test ends. A [trading plan and journal](/academy/article/trading-plan-and-journal) is the natural place to keep that list.

</ProTip>

## Reading the results honestly

If the forward test roughly matches the backtest, you have some evidence that the edge was real and that you can execute it. If it is much worse, first check whether you followed the rules; execution gaps are common. If you did follow them, the backtest may have been overfitted, or market conditions may have changed. Dropping a strategy that fails its test is a success of the process, not a failure.

## Conclusion

Backtesting tells you whether written rules had an edge on past data; forward-testing tells you whether that edge survives fresh prices and your own execution. Write rules a stranger could follow, measure expectancy in R after costs, watch for the biases that flatter results, and forward-test on demo and then at small size before committing more. Even then, treat any edge as something to monitor, not something you own.
